import type {
    Device,
    DeviceCategory,
    DeviceStatus,
    Facility,
    Rack
} from '@/types'

import {
    CATEGORY_ORDER,
    STATUS_META,
    STATUS_ORDER,
    THERMAL_STOPS
} from '@/constants/inventory'

// Client-safe helpers for reading inventory rows. Category and status columns are
// free text in the CMS, so everything that drives logic goes through a normalizer.

export const toCategory = (value?: string | null): DeviceCategory => {
    const v = (value ?? '').trim().toLowerCase()

    if (v.startsWith('net') || v.includes('switch') || v.includes('router') || v.includes('firewall')) return 'network'
    if (v.startsWith('stor') || v.includes('san') || v.includes('array') || v.includes('backup') || v.includes('tape')) return 'storage'

    return 'server'
}

export const toStatus = (value?: string | null): DeviceStatus => {
    const v = (value ?? '').trim().toLowerCase()

    if (v.startsWith('on') || v === 'ok' || v === 'up' || v === 'healthy' || v === 'active') return 'online'
    if (v.startsWith('warn') || v.startsWith('degrad')) return 'warning'
    if (v.startsWith('crit') || v.startsWith('fail') || v.startsWith('err') || v === 'down') return 'critical'
    if (v.startsWith('maint') || v.startsWith('service')) return 'maintenance'
    if (v.startsWith('off') || v.startsWith('decom') || v.startsWith('power')) return 'offline'

    return 'unknown'
}

export const severityOf = (status: DeviceStatus): number => STATUS_META[status].severity

export const worstStatus = (statuses: DeviceStatus[]): DeviceStatus => {
    let worst: DeviceStatus = 'online'

    for (const status of statuses) {
        if (status === 'critical') return 'critical'
        if (status === 'warning') worst = 'warning'
        else if (worst === 'online' && (status === 'maintenance' || status === 'offline')) worst = status
    }

    return worst
}

export type DeviceVisual =
    | 'server'
    | 'gpu'
    | 'blade'
    | 'switch'
    | 'patch'
    | 'fiber'
    | 'chassis'
    | 'router'
    | 'appliance'
    | 'array'
    | 'shelf'
    | 'node'
    | 'tape'

// Picks the 3D front panel for a device from its type, falling back to its category.
export const deviceVisual = (device: Pick<Device, 'type' | 'category'>): DeviceVisual => {
    const t = device.type.toLowerCase()

    if (t.includes('fiber') && t.includes('patch')) return 'fiber'
    if (t.includes('patch')) return 'patch'
    if (t.includes('tape')) return 'tape'
    if (t.includes('gpu')) return 'gpu'
    if (t.includes('blade')) return 'blade'
    if (t.includes('shelf')) return 'shelf'
    if (t.includes('storage node') || t.includes('object')) return 'node'
    if (t.includes('array') || t.includes('storage controller') || t.includes('backup') || t.includes('nas')) return 'array'
    if (t.includes('chassis') || t.includes('director') || t.includes('optical') || t.includes('transport')) return 'chassis'
    if (t.includes('router')) return 'router'
    if (t.includes('firewall') || t.includes('balancer') || t.includes('broker') || t.includes('appliance')) return 'appliance'
    if (t.includes('switch') || t.includes('interconnect') || t.includes('console')) return 'switch'

    const category = toCategory(device.category)

    if (category === 'network') return 'switch'
    if (category === 'storage') return 'array'

    return 'server'
}

export type InventoryStats = {
    racks: number
    devices: number
    byCategory: Record<DeviceCategory, number>
    byStatus: Record<DeviceStatus, number>
    powerKw: number
    capacityKw: number
    storageTb: number
    memoryTb: number
    avgTemp: number | null
    alerts: number
    usedUnits: number
    totalUnits: number
}

const emptyCategoryCount = (): Record<DeviceCategory, number> => ({ server: 0, network: 0, storage: 0 })

const emptyStatusCount = (): Record<DeviceStatus, number> => ({
    online: 0,
    warning: 0,
    critical: 0,
    maintenance: 0,
    offline: 0,
    unknown: 0
})

export const computeStats = (racks: Rack[], devices: Device[], facility: Facility | null): InventoryStats => {
    const byCategory = emptyCategoryCount()
    const byStatus = emptyStatusCount()

    let watts = 0
    let storageTb = 0
    let memoryGb = 0
    let tempSum = 0
    let tempCount = 0
    let usedUnits = 0

    for (const device of devices) {
        const category = toCategory(device.category)

        byCategory[category] += 1
        byStatus[toStatus(device.status)] += 1
        watts += device.power_w ?? 0
        usedUnits += device.u_height

        if (category === 'storage') storageTb += device.storage_tb ?? 0
        if (category === 'server') memoryGb += device.memory_gb ?? 0

        if (device.temperature_c !== null) {
            tempSum += device.temperature_c
            tempCount += 1
        }
    }

    const rackCapacity = racks.reduce((sum, rack) => sum + (rack.power_capacity_kw ?? 0), 0)

    return {
        racks: racks.length,
        devices: devices.length,
        byCategory,
        byStatus,
        powerKw: watts / 1000,
        capacityKw: facility?.power_capacity_kw ?? rackCapacity,
        storageTb,
        memoryTb: memoryGb / 1024,
        avgTemp: tempCount ? tempSum / tempCount : null,
        alerts: byStatus.warning + byStatus.critical,
        usedUnits,
        totalUnits: racks.reduce((sum, rack) => sum + rack.units, 0)
    }
}

export type RackSummary = {
    deviceCount: number
    usedUnits: number
    powerKw: number
    avgTemp: number | null
    maxTemp: number | null
    worst: DeviceStatus
    byCategory: Record<DeviceCategory, number>
    byStatus: Record<DeviceStatus, number>
}

export const summarizeRack = (rack: Rack, devices: Device[]): RackSummary => {
    const byCategory = emptyCategoryCount()
    const byStatus = emptyStatusCount()
    const statuses: DeviceStatus[] = []

    let usedUnits = 0
    let watts = 0
    let tempSum = 0
    let tempCount = 0
    let maxTemp: number | null = null

    for (const device of devices) {
        const status = toStatus(device.status)

        byCategory[toCategory(device.category)] += 1
        byStatus[status] += 1
        statuses.push(status)
        usedUnits += device.u_height
        watts += device.power_w ?? 0

        if (device.temperature_c !== null) {
            tempSum += device.temperature_c
            tempCount += 1
            maxTemp = maxTemp === null ? device.temperature_c : Math.max(maxTemp, device.temperature_c)
        }
    }

    return {
        deviceCount: devices.length,
        usedUnits: Math.min(usedUnits, rack.units),
        powerKw: watts / 1000,
        avgTemp: tempCount ? tempSum / tempCount : null,
        maxTemp,
        worst: worstStatus(statuses),
        byCategory,
        byStatus
    }
}

// Group devices by rack id once, so panels and the 3D scene can look them up cheaply.
export const groupDevicesByRack = (devices: Device[]): Map<string, Device[]> => {
    const map = new Map<string, Device[]>()

    for (const device of devices) {
        if (!device.rack_id) continue

        const list = map.get(device.rack_id)

        if (list) list.push(device)
        else map.set(device.rack_id, [device])
    }

    for (const list of map.values()) list.sort((a, b) => b.u_position - a.u_position)

    return map
}

export const matchesSearch = (device: Device, rack: Rack | undefined, query: string): boolean => {
    const q = query.trim().toLowerCase()

    if (!q) return true

    const fields = [
        device.hostname,
        device.ip_address,
        device.model,
        device.vendor,
        device.serial_number,
        device.asset_tag,
        device.role,
        device.type,
        device.os,
        rack?.code,
        rack?.name
    ]

    return fields.some(field => field ? field.toLowerCase().includes(q) : false)
}

const hexToRgb = (hex: string): [number, number, number] => {
    const value = parseInt(hex.replace('#', ''), 16)

    return [(value >> 16) & 255, (value >> 8) & 255, value & 255]
}

const rgbToHex = (r: number, g: number, b: number): string => {
    const toHex = (n: number) => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, '0')

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

export const thermalColor = (temp: number | null): string => {
    if (temp === null) return '#475569'

    const stops = THERMAL_STOPS

    if (temp <= stops[0].temp) return stops[0].color
    if (temp >= stops[stops.length - 1].temp) return stops[stops.length - 1].color

    for (let i = 0; i < stops.length - 1; i++) {
        const a = stops[i]
        const b = stops[i + 1]

        if (temp >= a.temp && temp <= b.temp) {
            const t = (temp - a.temp) / (b.temp - a.temp)
            const [ar, ag, ab] = hexToRgb(a.color)
            const [br, bg, bb] = hexToRgb(b.color)

            return rgbToHex(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t)
        }
    }

    return stops[stops.length - 1].color
}

export const formatPower = (kw: number): string => {
    if (kw >= 1000) return `${(kw / 1000).toFixed(2)} MW`
    if (kw >= 100) return `${kw.toFixed(0)} kW`

    return `${kw.toFixed(1)} kW`
}

export const formatCapacity = (tb: number): string => {
    if (tb >= 1000) return `${(tb / 1000).toFixed(1)} PB`
    if (tb >= 10) return `${tb.toFixed(0)} TB`

    return `${tb.toFixed(2)} TB`
}

export const formatInteger = (value: number): string => new Intl.NumberFormat('en-US').format(Math.round(value))

export const unitRange = (device: Pick<Device, 'u_position' | 'u_height'>): string => {
    if (device.u_height <= 1) return `U${device.u_position}`

    return `U${device.u_position}-${device.u_position + device.u_height - 1}`
}

export const sortedCategories = (): DeviceCategory[] => [...CATEGORY_ORDER]

export const sortedStatuses = (): DeviceStatus[] => [...STATUS_ORDER]
